/**
 * Account — self-service settings for the signed-in user.
 *
 * Change-password flow (no server endpoint needed — Supabase authorizes it with
 * the user's own session):
 *   1. Re-authenticate with the current password (proves it's really them, so an
 *      unattended session can't have its password silently changed).
 *   2. supabase.auth.updateUser({ password }) sets the new one.
 */
import { useState, type FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/auth/AuthProvider";
import { PageHeader, Card, Button, TextField } from "@/components/ui";

export function Account() {
  const { user } = useAuth();
  const email = user?.email ?? "";

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus(null);
    if (next.length < 8) {
      setStatus({ kind: "err", msg: "New password must be at least 8 characters." });
      return;
    }
    if (next !== confirm) {
      setStatus({ kind: "err", msg: "New passwords don't match." });
      return;
    }
    setBusy(true);
    try {
      // 1. Verify the current password by re-authenticating.
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email,
        password: current,
      });
      if (signInErr) {
        setStatus({ kind: "err", msg: "Current password is incorrect." });
        return;
      }
      // 2. Set the new password (session-authorized).
      const { error: updErr } = await supabase.auth.updateUser({ password: next });
      if (updErr) {
        setStatus({ kind: "err", msg: updErr.message });
        return;
      }
      setStatus({ kind: "ok", msg: "Password updated. Use it next time you sign in." });
      setCurrent("");
      setNext("");
      setConfirm("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <PageHeader title="Account" subtitle={email} />
      <div className="p-5">
        <Card className="max-w-md">
          <h2 className="mb-3 text-sm font-semibold">Change password</h2>
          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <TextField
              label="Current password"
              type="password"
              required
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
            <TextField
              label="New password"
              type="password"
              required
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <TextField
              label="Confirm new password"
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            <Button type="submit" disabled={busy}>
              {busy ? "Saving…" : "Update password"}
            </Button>
            {status && (
              <p
                className={
                  status.kind === "ok" ? "text-sm text-emerald-600" : "text-sm text-red-600"
                }
              >
                {status.msg}
              </p>
            )}
          </form>
        </Card>
      </div>
    </div>
  );
}
